/**
 * Support Service
 * 
 * API calls for support ticket management.
 */
import api from '@/lib/api';
import type {
  SupportTicket,
  SupportTicketDetail,
  SupportTicketCreate,
  SupportTicketListResponse,
  TicketMessage,
  TicketMessageCreate,
  TicketStatus,
} from '@/types/support';

export const supportService = {
  // Get all tickets for current user
  getMyTickets: async (
    status?: TicketStatus,
    page: number = 1,
    pageSize: number = 20
  ): Promise<SupportTicketListResponse> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    
    const response = await api.get(`/support/tickets?${params.toString()}`);
    return response.data;
  },

  // Get a specific ticket with messages
  getTicket: async (ticketId: string): Promise<SupportTicketDetail> => {
    const response = await api.get(`/support/tickets/${ticketId}`);
    return response.data;
  },

  // Create a new ticket (with optional attachments)
  createTicket: async (data: SupportTicketCreate): Promise<SupportTicket> => {
    const { attachments, ...ticketData } = data;
    
    // Always use FormData since backend expects Form fields
    const formData = new FormData();
    formData.append('subject', ticketData.subject);
    formData.append('description', ticketData.description);
    formData.append('category', ticketData.category || 'general');
    formData.append('priority', ticketData.priority || 'medium');
    
    // Add attachments if any
    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }
    
    const response = await api.post('/support/tickets', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Add a message to a ticket (with optional attachments)
  addMessage: async (ticketId: string, data: TicketMessageCreate): Promise<TicketMessage> => {
    const { attachments, ...messageData } = data;
    
    // Always use FormData since backend expects Form fields
    const formData = new FormData();
    formData.append('message', messageData.message);
    formData.append('is_internal_note', String(messageData.is_internal_note || false));
    
    // Add attachments if any
    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }
    
    const response = await api.post(`/support/tickets/${ticketId}/messages`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get unread count
  getUnreadCount: async (): Promise<{ unread_count: number }> => {
    const response = await api.get('/support/tickets/unread/count');
    return response.data;
  },
};

export default supportService;
